import React from "react";

import CheckIcon from "@uui/Check";
import LinkIcon from "@uui/Link01";
import { isEmpty } from "lodash/fp";

import { Collapse, Typography } from "@mui/material";

import * as Events from "@app/common/access/ShareReport/events";
import useReportShare from "@app/common/access/ShareReport/hooks/useReportShare";
import LinkAccess from "@app/common/access/ShareReport/LinkAccess";
import {
  DisclaimerContainer,
  SharingButton,
} from "@app/common/access/ShareReport/styles";
import { getReportShareLink } from "@app/common/access/ShareReport/utils";
import { showToast } from "@app/common/hooks/useToasts";
import type {
  ApiEntityAccessView,
  GeneralAccess,
  GeneralAccessLevel,
  GeneralAccessPattern,
} from "@app/common/types";

import CopyButton from "@rui/CopyButton";
import { Row } from "@rui/Stack";

import { Footer } from "./styles";

interface ShareReportFooterProps {
  fullScreen: boolean;
  generalAccess: GeneralAccess;
  isActiveSession: boolean;
  isAddingUsers: boolean;
  isLoading: boolean;
  onClose: () => void;
  onShare: () => void;
  sessionId: string;
  needsEditAccess?: boolean;
}

const ShareReportFooter = ({
  fullScreen,
  generalAccess,
  isActiveSession,
  isAddingUsers,
  isLoading,
  onClose,
  onShare,
  sessionId,
  needsEditAccess,
}: ShareReportFooterProps) => {
  const { accessList, modifyAccessMutation, modifyGeneralAccessMutation } =
    useReportShare({ sessionId });

  const handleCopy = (copied: boolean) => {
    Events.shareReportCopyLink();
    if (copied && needsEditAccess) {
      let msg: string | null = null;
      if (generalAccess.accessPattern === "people_with_access") {
        msg =
          "Only people the report is currently shared with can access with a link.";
      } else if (generalAccess.accessPattern === "workspace_members") {
        msg = "Only members of the Workspace can access with a link.";
      }
      showToast({
        message: `Link copied to clipboard.${msg ? ` ${msg}` : null}`,
        severity: "success",
      });
    }
  };

  const handleDone = () => {
    Events.shareReportDone();
    onClose();
  };

  const handleLinkAccessChange = (
    generalAccessPattern: GeneralAccessPattern,
    accessLevel: GeneralAccessLevel,
    entitiesToUpdate: ApiEntityAccessView[] = []
  ) => {
    if (!sessionId) return;

    modifyGeneralAccessMutation.mutate(
      {
        generalAccess: generalAccessPattern,
        accessLevel,
        sessionId,
      },
      {
        onError: () => {
          showToast({
            message: "There was a problem. Try again or contact support.",
            severity: "error",
          });
        },
      }
    );

    if (!isEmpty(entitiesToUpdate)) {
      entitiesToUpdate.map((entity) => {
        modifyAccessMutation.mutate({
          entity: { ...entity, accessLevel },
          sessionId,
        });
      });
    }
  };

  return (
    <Footer spacing={1.5}>
      <Collapse in={!isAddingUsers} unmountOnExit>
        <LinkAccess
          accessList={accessList.entityAccess}
          onChange={handleLinkAccessChange}
          generalAccess={generalAccess}
          fullScreen={fullScreen}
          disabled={needsEditAccess}
        />
      </Collapse>
      <Collapse in={isAddingUsers && isActiveSession} unmountOnExit>
        <DisclaimerContainer>
          <Typography variant="bodySecondaryReg">
            This report will be generated and sent to participants once the
            meeting has ended.
          </Typography>
        </DisclaimerContainer>
      </Collapse>
      <Row variant="apart">
        <CopyButton
          copyValue={getReportShareLink(sessionId!, "share")}
          onClick={handleCopy}
          renderButton={({ onClick }, copied) => (
            <SharingButton
              variant="text"
              color={copied ? "success" : "primary"}
              onClick={onClick}
              startIcon={copied ? <CheckIcon /> : <LinkIcon />}
            >
              {copied ? "Copied!" : "Copy link"}
            </SharingButton>
          )}
        />
        {isAddingUsers ? (
          <SharingButton loading={isLoading} onClick={onShare}>
            Share
          </SharingButton>
        ) : (
          <SharingButton onClick={handleDone}>Done</SharingButton>
        )}
      </Row>
    </Footer>
  );
};

export default ShareReportFooter;
